import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Save, Package, Banknote, Wrench, Archive } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { equipmentAPI, usersAPI } from '../../services/api';
import { ImageCaptureField } from '../../components/ui';
import { resolveUploadUrl } from '../../utils/uploadUrl';
import AssetTabs from '../assets/AssetTabs';

export default function AddEditEquipment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const assetMode = location.pathname.includes('/dashboard/assets/');
  const basePath = assetMode ? '/dashboard/assets/fixed' : '/dashboard/operations/equipment';

  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [existing, setExisting] = useState({ next_service_due: null, last_service_date: null });
  const photoFileRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [form, setForm] = useState({
    equipment_name: '',
    equipment_type: '',
    brand: '',
    model: '',
    serial_number: '',
    location: '',
    purchase_date: '',
    purchase_cost: '',
    warranty_expiry: '',
    fuel_type: '',
    service_interval_days: '',
    service_interval_hours: '',
    status: 'ACTIVE',
    assigned_staff_id: '',
    disposal_status: '',
    disposal_date: '',
    disposal_reason: '',
    photo_url: '',
  });

  useEffect(() => {
    usersAPI.getAll().then((res) => setUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      equipmentAPI.get(id).then((res) => {
        const d = res.data;
        setExisting({
          next_service_due: d.next_service_due || null,
          last_service_date: d.last_service_date || null,
        });
        setForm({
          equipment_name: d.equipment_name || '',
          equipment_type: d.equipment_type || '',
          brand: d.brand || '',
          model: d.model || '',
          serial_number: d.serial_number || '',
          location: d.location || '',
          purchase_date: d.purchase_date ? d.purchase_date.slice(0, 10) : '',
          purchase_cost: d.purchase_cost ?? '',
          warranty_expiry: d.warranty_expiry ? d.warranty_expiry.slice(0, 10) : '',
          fuel_type: d.fuel_type || '',
          service_interval_days: d.service_interval_days ?? '',
          service_interval_hours: d.service_interval_hours ?? '',
          status: d.status || 'ACTIVE',
          assigned_staff_id: d.assigned_staff_id ?? '',
          disposal_status: d.disposal_status || '',
          disposal_date: d.disposal_date ? d.disposal_date.slice(0, 10) : '',
          disposal_reason: d.disposal_reason || '',
          photo_url: d.photo_url || '',
        });
        setPhotoUrl(d.photo_url ? resolveUploadUrl(d.photo_url) : '');
        photoFileRef.current = null;
      }).catch(console.error);
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const deriveNextServiceDue = (payload) => {
    if (existing.next_service_due) return null;
    const intervalDays = parseInt(payload.service_interval_days, 10);
    if (!intervalDays || intervalDays <= 0) return null;
    const anchorStr = existing.last_service_date || payload.purchase_date || new Date().toISOString();
    const anchor = new Date(anchorStr);
    if (Number.isNaN(anchor.getTime())) return null;
    anchor.setDate(anchor.getDate() + intervalDays);
    return anchor.toISOString().slice(0, 10);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let nextPhotoUrl = form.photo_url || null;
      if (photoFileRef.current) {
        const uploadRes = await equipmentAPI.uploadPhoto(photoFileRef.current);
        nextPhotoUrl = uploadRes?.data?.url || null;
      }
      const payload = { ...form, photo_url: nextPhotoUrl };
      if (payload.purchase_cost) payload.purchase_cost = parseFloat(payload.purchase_cost);
      if (payload.service_interval_days) payload.service_interval_days = parseInt(payload.service_interval_days, 10);
      if (payload.service_interval_hours) payload.service_interval_hours = parseFloat(payload.service_interval_hours);
      payload.assigned_staff_id = payload.assigned_staff_id ? parseInt(payload.assigned_staff_id, 10) : null;
      payload.disposal_status = payload.disposal_status || null;
      payload.disposal_date = payload.disposal_date || null;
      payload.disposal_reason = payload.disposal_reason || null;
      const derivedDue = deriveNextServiceDue(form);
      if (derivedDue) payload.next_service_due = derivedDue;
      if (isEdit) {
        await equipmentAPI.update(id, payload);
        navigate(`${basePath}/${id}`);
      } else {
        const res = await equipmentAPI.create(payload);
        navigate(`${basePath}/${res.data.id}`);
      }
    } catch (err) {
      alert(err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'ga-input w-full px-4 py-2 rounded-lg';
  const labelCls = 'block text-sm font-medium text-ga-text-muted mb-1';
  const sectionCls = 'text-sm font-semibold uppercase tracking-wide text-ga-text-muted flex items-center gap-2';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to={basePath} aria-label="Back to asset list" className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-ga-text-primary">
            {isEdit ? 'Edit Fixed Asset' : 'Add Fixed Asset'}
          </h1>
        </div>

        {assetMode && <AssetTabs active="fixed" />}

        <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Package className="h-4 w-4" />Basic info</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="eq-name" className={labelCls}>Asset name *</label>
                <input id="eq-name" name="equipment_name" value={form.equipment_name} onChange={handleChange} required className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-type" className={labelCls}>Type</label>
                <input id="eq-type" name="equipment_type" value={form.equipment_type} onChange={handleChange} className={inputCls} placeholder="e.g. Mower, Roller" />
              </div>
              <div>
                <label htmlFor="eq-location" className={labelCls}>Location</label>
                <input id="eq-location" name="location" value={form.location} onChange={handleChange} className={inputCls} placeholder="e.g. Shed A, Main pitch" />
              </div>
              <div>
                <label htmlFor="eq-brand" className={labelCls}>Brand</label>
                <input id="eq-brand" name="brand" value={form.brand} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-model" className={labelCls}>Model</label>
                <input id="eq-model" name="model" value={form.model} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-serial" className={labelCls}>Serial number</label>
                <input id="eq-serial" name="serial_number" value={form.serial_number} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-assigned" className={labelCls}>Assigned to</label>
                <select id="eq-assigned" name="assigned_staff_id" value={form.assigned_staff_id} onChange={handleChange} className={inputCls}>
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.username}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <ImageCaptureField
                  label="Photo (optional)"
                  valueUrl={photoUrl}
                  attachedHint="Intake photo — useful for later condition checks"
                  onChange={({ file, url }) => {
                    photoFileRef.current = file;
                    if (!file) {
                      setPhotoUrl('');
                      setForm((f) => ({ ...f, photo_url: '' }));
                    } else {
                      setPhotoUrl(url);
                    }
                  }}
                />
              </div>
            </div>
          </section>

          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Banknote className="h-4 w-4" />Financial info</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="eq-purchase-date" className={labelCls}>Purchase date</label>
                <input id="eq-purchase-date" type="date" name="purchase_date" value={form.purchase_date} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-purchase-cost" className={labelCls}>Purchase cost (₹)</label>
                <input id="eq-purchase-cost" type="number" step="0.01" name="purchase_cost" value={form.purchase_cost} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-warranty" className={labelCls}>Warranty expiry</label>
                <input id="eq-warranty" type="date" name="warranty_expiry" value={form.warranty_expiry} onChange={handleChange} className={inputCls} />
              </div>
            </div>
          </section>

          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className={sectionCls}><Wrench className="h-4 w-4" />Maintenance</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="eq-fuel" className={labelCls}>Fuel type</label>
                <input id="eq-fuel" name="fuel_type" value={form.fuel_type} onChange={handleChange} className={inputCls} placeholder="e.g. Diesel, Petrol" />
              </div>
              <div>
                <label htmlFor="eq-interval-days" className={labelCls}>Service interval (days)</label>
                <input id="eq-interval-days" type="number" name="service_interval_days" value={form.service_interval_days} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-interval-hours" className={labelCls}>Service interval (hours)</label>
                <input id="eq-interval-hours" type="number" step="0.1" name="service_interval_hours" value={form.service_interval_hours} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label htmlFor="eq-status" className={labelCls}>Operational status</label>
                <select id="eq-status" name="status" value={form.status} onChange={handleChange} className={inputCls}>
                  <option value="ACTIVE">Active</option>
                  <option value="UNDER_REPAIR">Under Repair</option>
                  <option value="OUT_OF_SERVICE">Out of Service</option>
                </select>
              </div>
            </div>
          </section>

          {isEdit && (
            <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
              <h2 className={sectionCls}><Archive className="h-4 w-4" />Disposal</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="eq-disposal" className={labelCls}>Disposal status</label>
                  <select id="eq-disposal" name="disposal_status" value={form.disposal_status} onChange={handleChange} className={inputCls}>
                    <option value="">In service</option>
                    <option value="DISPOSED">Disposed</option>
                    <option value="SOLD">Sold</option>
                    <option value="WRITTEN_OFF">Written off</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="eq-disposal-date" className={labelCls}>Disposal date</label>
                  <input id="eq-disposal-date" type="date" name="disposal_date" value={form.disposal_date} onChange={handleChange} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="eq-disposal-reason" className={labelCls}>Reason</label>
                  <input id="eq-disposal-reason" name="disposal_reason" value={form.disposal_reason} onChange={handleChange} className={inputCls} placeholder="Optional notes" />
                </div>
              </div>
            </section>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="ga-btn-primary">
              <Save className="h-5 w-5" />
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => navigate(basePath)} className="ga-btn-ghost">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
